import React, { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, RefreshCw, PlusCircle, CheckCircle2, MinusCircle } from "lucide-react";
import libFloat from "../../../../../util/libFloat";
import Installment from "../../../../../components/installment/parameters";
import reqFormaPgto from "../../../../../services/reqMultiTenant/reqFormaPgto";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@mui/material";
import "../../../../../components/installment/table/style.css";
import {
	isFormaLiquidadaNoAto,
	isFormaDinheiro,
	validarSaldoParaAbatimento,
	calcularAbatimentoSequencial,
} from "./_utils/parcelas-rules";

export default function StepPagamento({
	totalAParcelar,
	totalOriginal = 0,
	originalParcelas = [],
	parcelas,
	setParcelas,
	tipoOperacao,
	setTipoOperacao,
	itens = [],
	complementos = [],
	venda = {},
}) {
	const formatCurrency = (val) => libFloat.strToCurrency(val || 0);
	const [formasPgto, setFormasPgto] = useState([]);

	// A diferença precisa ser calculada em cima do valor real da venda
	const diferenca = totalAParcelar - totalOriginal;
	const aumentou = diferenca > 0;

	// Valor efetivo que passamos ao faturamento
	const valorFaturarEfetivo = tipoOperacao === "REFAZER PARCELAMENTO" ? totalAParcelar : Math.abs(diferenca);

	// Abatimento parcial: valida saldo em aberto e simula o "de → para" que o backend aplicará
	const erroSaldoAbatimento =
		tipoOperacao === "ABATER PARCIALMENTE"
			? validarSaldoParaAbatimento(originalParcelas, Math.abs(diferenca))
			: null;

	const previewAbatimento =
		tipoOperacao === "ABATER PARCIALMENTE" && !erroSaldoAbatimento
			? calcularAbatimentoSequencial(originalParcelas, Math.abs(diferenca))
			: { ajustes: [], restanteNaoAbatido: 0 };

	// Busca as formas de pagamento na montagem do componente
	useEffect(() => {
		reqFormaPgto.listAll()
			.then((res) => setFormasPgto(res.data || []))
			.catch((err) => console.error("Erro ao buscar formas de pagamento:", err));
	}, []);

	// Regras de forma de pagamento portadas do legado (FRM_CAD_VENDAALTERACAO):
	// - Cartão Débito/Crédito e Pix são liquidados no ato: nascem PAGO ('NAO'), data de hoje,
	//   e o legado bloqueia explicitamente colocá-los como pendente.
	// - Dinheiro nasce pago mas o operador PODE alternar pra pendente (e aí a data vai pra +30).
	// - Vale (e demais) nasce pendente ('SIM') com vencimento futuro.
	// isFormaLiquidadaNoAto/isFormaDinheiro vêm de _utils/parcelas-rules (mesma fonte usada
	// na validação final, pra regra não divergir entre a tela e o submit).
	const ID_FORMA_DINHEIRO = 1;

	const hoje = () => new Date().toISOString().split("T")[0];
	const em30Dias = () => new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

	// Status ('SIM' pendente / 'NAO' pago) que a forma assume ao ser escolhida
	const statusPadraoDaForma = (idForma, formaNome) => {
		if (isFormaLiquidadaNoAto(idForma, formaNome)) return "NAO";
		if (isFormaDinheiro(idForma, formaNome)) return "NAO";
		return "SIM"; // Vale e demais nascem pendentes
	};

	// Data que acompanha o status: pago = hoje, pendente = +30 dias
	const dataPadraoDoStatus = (ativo) => (ativo === "SIM" ? em30Dias() : hoje());

	// Inicializa/Reseta parcelas com base na operação selecionada
	useEffect(() => {
		if (tipoOperacao === "CRIAR MAIS PARCELAS" && formasPgto.length > 0 && parcelas.length === 0) {
			const num = originalParcelas.length + 1;
			const padrao = formasPgto.find((f) => f.id_forma === ID_FORMA_DINHEIRO) || {
				id_forma: ID_FORMA_DINHEIRO,
				nome: "DINHEIRO",
			};
			const ativoPadrao = statusPadraoDaForma(padrao.id_forma, padrao.nome);

			setParcelas([
				{
					parcela: num,
					data: dataPadraoDoStatus(ativoPadrao),
					id_forma: padrao.id_forma,
					forma: padrao.nome,
					ativo: ativoPadrao,
					valor: Math.abs(diferenca),
				},
			]);
		} else if (tipoOperacao === "ABATER PARCIALMENTE") {
			setParcelas([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tipoOperacao, formasPgto, originalParcelas.length, diferenca, parcelas.length, setParcelas]);

	// Atualiza os campos da parcela única de ajuste (usado apenas em CRIAR MAIS PARCELAS)
	const handleUpdateParcelaUnica = (field, value) => {
		setParcelas((prev) => {
			if (prev.length === 0) return prev;
			const clone = [...prev];
			if (field === "id_forma") {
				const idForma = Number(value);
				const formaObj = formasPgto.find((f) => f.id_forma === idForma) || { id_forma: idForma, nome: "" };
				const novoAtivo = statusPadraoDaForma(idForma, formaObj.nome);
				clone[0] = {
					...clone[0],
					id_forma: idForma,
					forma: formaObj.nome,
					ativo: novoAtivo,
					// Trocar a forma reposiciona a data conforme o status resultante (mesma regra do legado)
					data: dataPadraoDoStatus(novoAtivo),
				};
			} else {
				clone[0] = { ...clone[0], [field]: value };
			}
			return clone;
		});
	};

	// Alterna Pago/Pendente — só permitido pra Dinheiro (o legado bloqueia reabrir Cartão/Pix).
	// A data acompanha o novo status: pendente vai pra +30 dias, pago volta pra hoje.
	const handleToggleStatusParcelaUnica = () => {
		setParcelas((prev) => {
			if (prev.length === 0) return prev;
			const atual = prev[0];
			if (!isFormaDinheiro(atual.id_forma, atual.forma)) return prev;

			const novoAtivo = atual.ativo === "SIM" ? "NAO" : "SIM";
			const clone = [...prev];
			clone[0] = { ...atual, ativo: novoAtivo, data: dataPadraoDoStatus(novoAtivo) };
			return clone;
		});
	};

	const handleSelecionarOperacao = (valor) => {
		setTipoOperacao(valor);
		setParcelas([]); // Limpa parcelas anteriores para forçar reinicialização
	};

	const handleParcelasGeradas = (data) => {
		const parcelasNormalizadas = (data.parcelas || []).map((p) => ({
			...p,
			ativo: p.ativo ? "SIM" : "NAO",
		}));
		setParcelas(parcelasNormalizadas);
	};

	const unica = parcelas[0] || null;
	
	const cellStyle = { padding: "8px", verticalAlign: "middle" };
	const colParcelaStyle = { ...cellStyle, width: "80px", minWidth: "80px" };
	const colValorStyle = { ...cellStyle, width: "180px", minWidth: "180px" };
	const colFormaStyle = { ...cellStyle, width: "260px", minWidth: "260px" };
	const colDataStyle = { ...cellStyle, width: "180px", minWidth: "180px" };
	const colStatusStyle = { ...cellStyle, width: "140px", minWidth: "140px", textAlign: "center" };

	return (
		<div className="space-y-6">
			{/* Banner Informativo */}
			<div className="bg-blue-50/50 border border-blue-100 p-4 rounded-2xl">
				<h3 className="text-sm font-bold text-blue-800 uppercase tracking-wider mb-1">Passo 2: Formas de Pagamento</h3>
				<p className="text-xs text-blue-600">
					{aumentou
						? "Os produtos adicionados aumentaram o valor da venda. Escolha como cobrar essa diferença."
						: "A remoção de produtos reduziu o valor da venda. Escolha como ajustar as parcelas."}
				</p>
			</div>

			{/* Resumo Antes / Depois / Diferença */}
			<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
				<div className="rounded-2xl border border-gray-150 bg-gray-50 p-4">
					<span className="block text-[10px] font-bold uppercase text-gray-400">Valor Original da Venda</span>
					<span className="text-lg font-black text-gray-700">{formatCurrency(totalOriginal)}</span>
					{originalParcelas.length > 0 && (
						<span className="mt-0.5 block text-[10px] text-gray-400">
							{originalParcelas.length} parcela(s) atual(is)
						</span>
					)}
				</div>
				<div className="rounded-2xl border border-blue-100 bg-blue-50/50 p-4">
					<span className="block text-[10px] font-bold uppercase text-blue-400">Novo Total da Venda</span>
					<span className="text-lg font-black text-blue-600">{formatCurrency(totalAParcelar)}</span>
				</div>
				<div className={`rounded-2xl border p-4 ${aumentou ? "border-emerald-100 bg-emerald-50/50" : "border-red-100 bg-red-50/50"}`}>
					<span className={`flex items-center gap-1 text-[10px] font-bold uppercase ${aumentou ? "text-emerald-500" : "text-red-500"}`}>
						{aumentou ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
						{aumentou ? "Aumento a Cobrar" : "Redução a Ajustar"}
					</span>
					<span className={`text-lg font-black ${aumentou ? "text-emerald-600" : "text-red-600"}`}>
						{formatCurrency(Math.abs(diferenca))}
					</span>
				</div>
			</div>

			{/* Escolha da Operação */}
			<div className="space-y-2">
				<span className="block text-xs font-bold uppercase text-gray-500">Como deseja faturar no financeiro?</span>
				<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
					{aumentou ? (
						<button
							type="button"
							onClick={() => handleSelecionarOperacao("CRIAR MAIS PARCELAS")}
							className={`flex items-start gap-3 rounded-2xl border p-4 text-left transition-all ${
								tipoOperacao === "CRIAR MAIS PARCELAS"
									? "border-blue-500 bg-blue-50/60 ring-1 ring-blue-500"
									: "border-gray-200 hover:border-blue-200 hover:bg-blue-50/30"
							}`}
						>
							<div className={`mt-0.5 rounded-xl p-2 ${tipoOperacao === "CRIAR MAIS PARCELAS" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-400"}`}>
								{tipoOperacao === "CRIAR MAIS PARCELAS" ? <CheckCircle2 size={16} /> : <PlusCircle size={16} />}
							</div>
							<div className="min-w-0">
								<p className="text-sm font-bold text-gray-800">Cobrar somente a diferença</p>
								<p className="text-xs text-gray-500">
									Mantém as parcelas atuais e gera parcela(s) extra só do valor a mais: <strong>{formatCurrency(Math.abs(diferenca))}</strong>.
								</p>
							</div>
						</button>
					) : (
						<button
							type="button"
							disabled={true}
							className="flex items-start gap-3 rounded-2xl border border-gray-100 p-4 text-left opacity-50 cursor-not-allowed bg-gray-50/50"
						>
							<div className="mt-0.5 rounded-xl p-2 bg-gray-100 text-gray-400">
								<MinusCircle size={16} />
							</div>
							<div className="min-w-0">
								<p className="text-sm font-bold text-gray-400">Abater a diferença das parcelas (Desabilitado)</p>
								<p className="text-xs text-gray-400">
									Reduz o valor das parcelas atuais em aberto (baixa parcial) na quantia de: <strong>-{formatCurrency(Math.abs(diferenca))}</strong>.
								</p>
							</div>
						</button>
					)}

					<button
						type="button"
						onClick={() => handleSelecionarOperacao("REFAZER PARCELAMENTO")}
						className={`flex items-start gap-3 rounded-2xl border p-4 text-left transition-all ${
							tipoOperacao === "REFAZER PARCELAMENTO"
								? "border-blue-500 bg-blue-50/60 ring-1 ring-blue-500"
								: "border-gray-200 hover:border-blue-200 hover:bg-blue-50/30"
						}`}
					>
						<div className={`mt-0.5 rounded-xl p-2 ${tipoOperacao === "REFAZER PARCELAMENTO" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-400"}`}>
							{tipoOperacao === "REFAZER PARCELAMENTO" ? <CheckCircle2 size={16} /> : <RefreshCw size={16} />}
						</div>
						<div className="min-w-0">
							<p className="text-sm font-bold text-gray-800">Refazer todo o parcelamento</p>
							<p className="text-xs text-gray-500">
								Substitui as parcelas atuais por um novo parcelamento do valor total: <strong>{formatCurrency(totalAParcelar)}</strong>.
							</p>
						</div>
					</button>
				</div>
			</div>

			{/* Faturamento Dinâmico */}
			{tipoOperacao === "REFAZER PARCELAMENTO" && (
				<div className="rounded-2xl border border-gray-150 bg-white p-6 shadow-sm">
					<div className="mb-4">
						<span className="block text-xs font-extrabold uppercase text-gray-400">Gerar parcelas sobre:</span>
						<span className="text-xl font-black text-emerald-600">{formatCurrency(valorFaturarEfetivo)}</span>
					</div>
					{/* hideSubmit: aqui o fluxo é "Gerar Parcelas → Confirmar e Salvar" (botão do rodapé
					    do modal), então o "Confirmar Parcelas" intermediário do componente é escondido —
					    as parcelas geradas chegam ao pai automaticamente via CallBackInstallments. */}
					<Installment
						installmentValue={valorFaturarEfetivo}
						CallBackInstallments={handleParcelasGeradas}
						hideSubmit
					/>
				</div>
			)}

			{tipoOperacao === "CRIAR MAIS PARCELAS" && unica && (
				<div className="rounded-2xl border border-gray-150 bg-white p-6 shadow-sm space-y-4">
					<div>
						<span className="block text-xs font-extrabold uppercase text-gray-400 font-bold">Parcela de ajuste gerada:</span>
					</div>

					<TableContainer>
						<Table>
							<TableHead className="color-table">
								<TableRow sx={{ height: "48px" }}>
									<TableCell sx={{ ...colParcelaStyle, color: "white" }}>Parcela</TableCell>
									<TableCell sx={{ ...colValorStyle, color: "white" }}>Valor</TableCell>
									<TableCell sx={{ ...colFormaStyle, color: "white" }}>Forma de Pagamento</TableCell>
									<TableCell sx={{ ...colDataStyle, color: "white" }}>Data</TableCell>
									<TableCell sx={{ ...colStatusStyle, color: "white" }}>Status</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								<TableRow hover sx={{ height: "57px" }}>
									{/* Parcela */}
									<TableCell sx={colParcelaStyle}>
										<span className="text-sm font-medium pl-2">{unica.parcela}</span>
									</TableCell>

									{/* Valor (usando os mesmos estilos inline exatos do custom-select para a borda e cantos) */}
									<TableCell sx={colValorStyle}>
										<input
											type="text"
											value={formatCurrency(unica.valor)}
											disabled
											style={{
												height: "40px",
												fontSize: "0.875rem",
												border: "1px solid #ccc",
												borderRadius: "4px",
												padding: "0 10px",
												backgroundColor: "#f9fafb",
												color: "#6b7280",
											}}
											className="w-full font-bold cursor-not-allowed box-border"
										/>
									</TableCell>

									{/* Forma de Pagamento */}
									<TableCell sx={colFormaStyle}>
										<select
											value={unica.id_forma}
											onChange={(e) => handleUpdateParcelaUnica("id_forma", parseInt(e.target.value))}
											className="custom-select w-full"
											style={{ height: "40px", marginTop: "2px", backgroundColor: "#fff" }}
										>
											{formasPgto.map((f) => (
												<option key={f.id_forma} value={f.id_forma}>
													{f.nome}
												</option>
											))}
										</select>
									</TableCell>

									{/* Data — Cartão/Pix são liquidados no ato (data fixa em hoje, sem edição),
									    igual ao legado. Dinheiro/Vale permitem escolher o vencimento. */}
									<TableCell sx={colDataStyle}>
										{isFormaLiquidadaNoAto(unica.id_forma, unica.forma) ? (
											<span className="text-sm text-gray-500 pl-2">
												{unica.data ? unica.data.split("-").reverse().join("/") : "—"}
											</span>
										) : (
											<input
												type="date"
												value={unica.data}
												onChange={(e) => handleUpdateParcelaUnica("data", e.target.value)}
												onClick={(e) => e.target.showPicker()}
												className="custom-select w-full"
												style={{
													height: "40px",
													fontSize: "0.875rem",
													border: "1px solid #ccc",
													borderRadius: "4px",
													padding: "0 10px",
												}}
											/>
										)}
									</TableCell>

									{/* Status — só Dinheiro pode alternar Pago/Pendente. O legado bloqueia
									    explicitamente reabrir Cartão/Pix ("Não é possível colocar uma parcela
									    com essa forma de pagamento como pendente!"), então aqui nem é clicável. */}
									<TableCell sx={colStatusStyle}>
										<div className="flex justify-center items-center">
											{isFormaDinheiro(unica.id_forma, unica.forma) ? (
												<div
													className={`badge_status ${unica.ativo === "SIM" ? "bg_red" : "bg_green"}`}
													style={{ cursor: "pointer" }}
													onClick={handleToggleStatusParcelaUnica}
													title="Clique para alternar entre Pago e Pendente"
												>
													{unica.ativo === "SIM" ? "Pendente" : "Pago"}
												</div>
											) : (
												<div
													className={`badge_status ${unica.ativo === "SIM" ? "bg_red" : "bg_green"}`}
													style={{ cursor: "default" }}
													title={
														isFormaLiquidadaNoAto(unica.id_forma, unica.forma)
															? "Cartão e Pix são liquidados no ato — não podem ficar pendentes"
															: undefined
													}
												>
													{unica.ativo === "SIM" ? "Pendente" : "Pago"}
												</div>
											)}
										</div>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
				</div>
			)}

			{tipoOperacao === "ABATER PARCIALMENTE" && (
				<div className="rounded-2xl border border-red-100 bg-red-50/20 p-6 space-y-3">
					<div>
						<span className="block text-xs font-extrabold uppercase text-red-500">Valor do abatimento parcial a ser aplicado:</span>
						<span className="text-xl font-black text-red-600">-{formatCurrency(Math.abs(diferenca))}</span>
					</div>
					<p className="text-xs text-gray-600 leading-relaxed">
						Nenhuma nova parcela será criada. Ao salvar, a diferença de <strong>-{formatCurrency(Math.abs(diferenca))}</strong>
						será abatida das parcelas em aberto, na ordem, até se esgotar (mesma mecânica do sistema antigo).
					</p>

					{/* Preview do abatimento: mostra parcela a parcela o "de → para" que o backend vai aplicar */}
					{erroSaldoAbatimento ? (
						<div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs font-bold text-red-700">
							{erroSaldoAbatimento}
						</div>
					) : (
						previewAbatimento.ajustes.length > 0 && (
							<div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
								<table className="w-full text-left text-xs">
									<thead>
										<tr className="border-b border-gray-100 bg-gray-50 text-[10px] font-bold uppercase text-gray-500">
											<th className="p-2.5">Parcela</th>
											<th className="p-2.5 text-right">Valor Atual</th>
											<th className="p-2.5 text-right">Abatimento</th>
											<th className="p-2.5 text-right">Fica Com</th>
											<th className="p-2.5 text-center">Situação</th>
										</tr>
									</thead>
									<tbody>
										{previewAbatimento.ajustes.map((a, idx) => (
											<tr key={idx} className="border-b border-gray-50 last:border-0">
												<td className="p-2.5 font-bold text-gray-700">{a.parcela ?? idx + 1}</td>
												<td className="p-2.5 text-right text-gray-600">{formatCurrency(a.valor_anterior)}</td>
												<td className="p-2.5 text-right font-bold text-red-600">-{formatCurrency(a.abatido)}</td>
												<td className="p-2.5 text-right font-bold text-gray-800">{formatCurrency(a.valor_novo)}</td>
												<td className="p-2.5 text-center">
													<span
														className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase ${
															a.ativo === "DEL"
																? "bg-gray-200 text-gray-600"
																: "bg-amber-100 text-amber-700"
														}`}
													>
														{a.ativo === "DEL" ? "Quitada" : "Em aberto"}
													</span>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)
					)}
				</div>
			)}

			{/* Visualizador de Payload Provisório para Debug */}
			<div className="mt-8 rounded-2xl border border-dashed border-gray-300 bg-gray-50/50 p-4">
				<details className="group">
					<summary className="flex cursor-pointer items-center justify-between text-xs font-bold uppercase tracking-wider text-gray-500 select-none">
						<span>🔍 Visualizar Payload de Envio (Debug Provisório)</span>
						<span className="transition-transform duration-200 group-open:rotate-180">▼</span>
					</summary>
					<div className="mt-4 overflow-x-auto rounded-xl bg-gray-900 p-4 text-[11px] font-mono leading-relaxed text-emerald-400 shadow-inner">
						<pre>
							{JSON.stringify(
								{
									venda: {
										id_venda: venda?.id_venda,
										id_empresa: venda?.id_empresa,
										valor_original: totalOriginal,
										valor_novo: totalAParcelar,
										diferenca
									},
									tipoOperacao,
									produtos: itens
										.filter(i => i._novo || i._removido || i._qtde_alterada)
										.map(i => {
											const itemComps = (complementos || []).filter(c => c.id_venda_item === i.id_venda_item);
											const isGradePai = i.grade || (itemComps.length > 0 && Number(i.valor_total ?? i.valortotal ?? i.valor ?? 0) === 0);
											return {
												id_venda_item: i.id_venda_item,
												id_produto: i.id_produto,
												nome: i.nome,
												qtde: i.qtde,
												valor_unitario: i.valor_unitario,
												valor: i.valor,
												desconto: i.desconto,
												grade: i.grade,
												tipo: isGradePai ? "grade" : "normal",
												_novo: i._novo,
												_removido: i._removido,
												_qtde_alterada: i._qtde_alterada
											};
										}),
									complementos: complementos
										.filter(c => c._novo || c._removido || c._qtde_alterada)
										.map(c => {
											const compPai = itens.find(i => i.id_venda_item === c.id_venda_item);
											const isGradeComp = (compPai && Number(compPai.valor_total ?? compPai.valortotal ?? compPai.valor ?? 0) === 0) 
												|| String(c.tipo || "").toUpperCase() === "GRADE" 
												|| c.is_grade 
												|| c.IS_GRADE;
											return {
												id_venda_item: c.id_venda_item,
												id_produto: c.id_produto,
												id_complementos_item: c.id_complementos_item,
												nome: c.nome,
												qtde: c.qtde,
												valorunit: c.valorunit,
												valor: c.valor,
												tipo: isGradeComp ? "grade" : (c.tipo || "adicional"),
												_novo: c._novo,
												_removido: c._removido,
												_qtde_alterada: c._qtde_alterada
											};
										}),
									parcelasGeradas: parcelas,
								},
								null,
								2
							)}
						</pre>
					</div>
				</details>
			</div>
		</div>
	);
}
